import { RootState } from 'types'
import { produce } from 'immer'
import { Action } from 'redux/actions'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { WorkspaceActions } from '../actions/workspace'
import workspaceSessionStorage from 'utils/workspaceSessionStorage'

export default function reduce(
  state: RootState['workspace'] = {
    workspaces: [],
    currentWorkspaceId: ALMANAC_CORE_WORKSPACE_ID,
  },
  action: Action,
): RootState['workspace'] {
  const saveAndReturnState = (newState: RootState['workspace']) => {
    workspaceSessionStorage.setWorkspaces(newState)
    return newState
  }

  switch (action.type) {
    case 'WORKSPACES_SET': {
      return saveAndReturnState({
        ...state,
        workspaces: action.payload,
      })
    }

    case 'CURRENT_WORKSPACE_ID_SET': {
      return saveAndReturnState({
        ...state,
        currentWorkspaceId: action.payload,
      })
    }

    case WorkspaceActions.SET_WORKSPACE_HOMEPAGE_DOC: {
      return {
        ...state,
        workspaces: state.workspaces.map((w) => {
          if (w.key === action.payload.workspaceKey) {
            return { ...w, ...{ homepage_doc: action.payload.doc } }
          } else {
            return w
          }
        }),
      }
    }

    case WorkspaceActions.SET_WORKSPACE_PAYING: {
      return {
        ...state,
        workspaces: state.workspaces.map((w) => {
          if (w.key === action.payload.workspaceKey) {
            return {
              ...w,
              ...{ paying: action.payload.paying, can_start_free_trial: action.payload.can_start_free_trial },
            }
          } else {
            return w
          }
        }),
      }
    }

    case WorkspaceActions.SET_WORKSPACE_COMPANY_SIZE_ENUM: {
      return {
        ...state,
        workspaces: state.workspaces.map((w) => {
          if (w.key === action.payload.workspaceKey) {
            return { ...w, ...{ company_size_enum: action.payload.company_size_enum } }
          } else {
            return w
          }
        }),
      }
    }

    case 'UPDATE_GITHUB_INTEGRATION': {
      return {
        ...state,
        workspaces: state.workspaces.map((w) => {
          if (w.id === action.payload.workspaceId) {
            return { ...w, ...{ github_integration_enabled: action.payload.githubIntegrationEnabled } }
          } else {
            return w
          }
        }),
      }
    }

    case 'MARK_WORKSPACE_AS_SEEN': {
      return produce(state, (draft) => {
        const workspaceIndex = state.workspaces.findIndex(({ key }) => key === action.payload)
        const workspace = draft.workspaces[workspaceIndex]
        if (workspace.is_new) workspace.isFirstView = true
        workspace.is_new = false
      })
    }

    case 'SET_IS_FIRST_VIEW': {
      return produce(state, (draft) => {
        const workspaceIndex = state.workspaces.findIndex(({ key }) => key === action.payload.key)
        const workspace = draft.workspaces[workspaceIndex]
        workspace.isFirstView = action.payload.isFirstView
      })
    }

    case WorkspaceActions.UPSERT_MEMBER_PROFILE: {
      return {
        ...state,
        workspaces: state.workspaces.map((w) => {
          if (w.id === action.payload.workspaceId) {
            return { ...w, ...{ profile: action.payload.profile } }
          } else {
            return w
          }
        }),
      }
    }

    case WorkspaceActions.UPDATE_THEME: {
      return produce(state, (draft) => {
        const workspaceIndex = state.workspaces.findIndex(({ key }) => key === action.payload.workspaceKey)
        const workspace = draft.workspaces[workspaceIndex]
        workspace.theme = {
          hue: action.payload.theme.hue ?? undefined,
          preset: action.payload.theme.preset ?? undefined,
          overrides: action.payload.theme.overrides ?? undefined,
        }
      })
    }

    case WorkspaceActions.UPDATE_COLOR_SCHEME: {
      return produce(state, (draft) => {
        const workspaceIndex = state.workspaces.findIndex(({ key }) => key === action.payload.workspaceKey)
        const workspace = draft.workspaces[workspaceIndex]
        workspace.color_scheme = action.payload.color_scheme
      })
    }

    case WorkspaceActions.UPDATE_APP_LOGO_URL: {
      return produce(state, (draft) => {
        const workspaceIndex = state.workspaces.findIndex(({ key }) => key === action.payload.workspaceKey)
        const workspace = draft.workspaces[workspaceIndex]
        workspace.app_logo_url = action.payload.app_logo_url
      })
    }

    default:
      return state
  }
}
